import type { ReactNode } from 'react';

type PitchShellProps = {
  /** The starting XI rows (GK → FWD). */
  xi: ReactNode;
  /** The bench row. */
  bench: ReactNode;
};

/**
 * The static pitch chrome shared by the interactive builder and the
 * read-only reveal pitch: a bordered half-pitch with aspect-true SVG goal
 * box + centre-circle markings, plus the bench strip beneath. Keeping this
 * in one place stops the two pitches drifting visually.
 */
const PitchShell = ({ xi, bench }: PitchShellProps) => (
  <div className="mt-6 max-w-[600px] w-full">
    {/* The pitch is a bordered rectangle (same stroke as the bench). The goal
        box and centre-circle half are separate SVGs scaled to a % of the
        pitch width with their natural aspect ratio preserved, so the box and
        circle stay true (no stretching) as the pitch resizes. */}
    <div className="relative overflow-hidden rounded-lg border-2 border-[#1FD0FF] bg-[#1FD0FF]/5">
      {/* Goal box: penalty area + 6-yard box + penalty arc, centred on the
          top edge. Width ≈ penalty-area-to-pitch ratio (402.2/680). */}
      <svg
        className="pointer-events-none absolute left-1/2 top-0 w-[59%] -translate-x-1/2"
        viewBox="139 0 404.2 216"
        fill="none"
        preserveAspectRatio="xMidYMin meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M140.4 1V166H542.6V1M250.4 1V56H432.6V1M267.88 166C298.26 206.38 355.62 214.49 396.01 184.11C402.87 178.95 408.96 172.85 414.12 166"
          stroke="#1FD0FF"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {/* Centre-circle half, centred on the bottom edge (the halfway line).
          Width ≈ centre-circle-diameter-to-pitch ratio (183/680). */}
      <svg
        className="pointer-events-none absolute bottom-0 left-1/2 w-[27%] -translate-x-1/2"
        viewBox="248 433 187 94"
        fill="none"
        preserveAspectRatio="xMidYMax meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M249.5 526C249.5 475.466 290.466 434.5 341 434.5C391.534 434.5 432.5 475.466 432.5 526"
          stroke="#1FD0FF"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="relative flex flex-col gap-y-6 px-2 pt-5 pb-4 sm:px-4 sm:gap-y-8">
        {xi}
      </div>
    </div>
    <div className="mt-2 rounded-lg border-2 border-[#1FD0FF] bg-[#1FD0FF]/5 px-2 pt-1 pb-2 sm:px-4">
      <p className="text-center text-xxs font-bold tracking-wider text-black/90 mb-1">
        BENCH
      </p>
      {bench}
    </div>
  </div>
);

export default PitchShell;
